/*
 * Relay GraphQL `Time` / `Date` scalar brands and trusted constructors
 *
 * Trusted type assertions via: timeScalarFromDateUtc, dateScalarFromUtcDate / dateScalarFromYmdParts,
 * dateScalarFromValidYmdString. Do not assign arbitrary strings; use widen helpers or these APIs.
 */
declare const timeScalarBrand: unique symbol;

declare const dateScalarBrand: unique symbol;

type TimeString = typeof timeScalarBrand;

type DateString = typeof dateScalarBrand;

/** GraphQL `Time` scalar: ISO-8601 instant string; mint only via {@link timeScalarFromDateUtc}. */
export type TimeScalar = TimeString & { readonly [timeScalarBrand]: true };

/** GraphQL `Date` scalar: `YYYY-MM-DD` only; mint only via the `dateScalarFrom*` functions in this module. */
export type DateScalar = DateString & { readonly [dateScalarBrand]: true };

const YMD_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad2 = (value: number): string => String(value).padStart(2, '0');

const assertValidDateParts = (
  year: number,
  month: number,
  day: number,
): void => {
  const utcDate = new Date(Date.UTC(year, month - 1, day));

  if (
    utcDate.getUTCFullYear() !== year ||
    utcDate.getUTCMonth() !== month - 1 ||
    utcDate.getUTCDate() !== day
  ) {
    throw new Error('DateScalar must be a valid ISO date (YYYY-MM-DD)');
  }
};

/**
 * Builds a {@link DateScalar} from integer calendar parts. Prefer this when you already
 * validated year/month/day (e.g. local month boundaries) so the result is trusted without re-parsing a string.
 */
export const dateScalarFromYmdParts = (
  year: number,
  month: number,
  day: number,
): DateScalar => {
  assertValidDateParts(year, month, day);

  return `${year}-${pad2(month)}-${pad2(day)}` as unknown as DateScalar;
};

/**
 * Trusted: takes a `Date` intended as a **UTC calendar day** and returns that day as `YYYY-MM-DD`.
 * Uses UTC getters so the branded value matches the API `Date` scalar (date-only, no local offset shift).
 */
export const dateScalarFromUtcDate = (date: Date): DateScalar =>
  dateScalarFromYmdParts(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  );

/**
 * Trusted boundary for **untrusted** text that should be a GraphQL `Date`: requires strict
 * `YYYY-MM-DD` and a real calendar date (rejects `2026-02-31`). Use for FormData / query params
 * before sending variables; throws if the input is not a valid date scalar.
 */
export const dateScalarFromValidYmdString = (value: string): DateScalar => {
  const match = value.match(YMD_REGEX);

  if (!match) {
    throw new Error('DateScalar must match YYYY-MM-DD');
  }

  const year = Number(match[1]);

  const month = Number(match[2]);

  const day = Number(match[3]);

  return dateScalarFromYmdParts(year, month, day);
};

/**
 * Trusted: serialises a real JavaScript `Date` to the API `Time` wire form (`Date#toISOString()`).
 * Use this (or a `Date` built from a known-good instant) for outbound Relay variables — not for labelling arbitrary strings.
 */
export const timeScalarFromDateUtc = (date: Date): TimeScalar =>
  date.toISOString() as unknown as TimeScalar;

/**
 * Widens a branded `TimeScalar` to `string` without changing bytes (e.g. DOM keys, timers).
 * Does **not** validate; only call on values that were already minted via {@link timeScalarFromDateUtc}.
 */
export const timeScalarToPlainString = (value: TimeScalar): string =>
  value as unknown as string;

/**
 * Display helper: scalar string or empty when nullish. Same bytes as the scalar when present;
 * does not parse or validate wire format.
 */
export const timeScalarOrEmpty = (
  value: TimeScalar | null | undefined,
): string => (value == null ? '' : (value as unknown as string));

/**
 * Returns the scalar when present and non-empty on the wire; otherwise `null`.
 * Use for optional `Time` fields before passing to client formatters (no `as TimeScalar`).
 */
export const timeScalarOrNull = (
  value: TimeScalar | null | undefined,
): TimeScalar | null => {
  if (value == null || timeScalarOrEmpty(value) === '') {
    return null;
  }

  return value;
};

/** Widens {@link DateScalar} to the same `YYYY-MM-DD` string at runtime (for non-Relay APIs). */
export const dateScalarToPlainString = (value: DateScalar): string =>
  value as unknown as string;

/** Same as {@link dateScalarToPlainString} but returns `''` when the value is nullish. */
export const dateScalarOrEmpty = (
  value: DateScalar | null | undefined,
): string => (value == null ? '' : (value as unknown as string));
